/**
 * Shared helpers for repeatable instance lists (#1154).
 */

import type {
  GroupedRepeatableInstances,
  RepeatableInstance,
  RepeatableInstanceStatus,
  RepeatableListSection,
} from './types';

export function normalizeInstanceStatus(
  status: RepeatableInstanceStatus | string | undefined,
): RepeatableInstanceStatus {
  return status === 'existing' ? 'existing' : 'new';
}

export function instanceStatusToTypeLabel(
  status: RepeatableInstanceStatus | string | undefined,
  options: readonly [string, string] = ['Existing', 'New'],
): string {
  return normalizeInstanceStatus(status) === 'existing'
    ? options[0]
    : options[1];
}

export function typeLabelToInstanceStatus(
  label: string,
  existingLabel = 'Existing',
): RepeatableInstanceStatus {
  return label === existingLabel ? 'existing' : 'new';
}

export function groupByStatus<T extends RepeatableInstance>(
  instances: T[],
): GroupedRepeatableInstances<T> {
  return {
    existing: instances.filter(
      item => normalizeInstanceStatus(item.instanceStatus) === 'existing',
    ),
    new: instances.filter(
      item => normalizeInstanceStatus(item.instanceStatus) === 'new',
    ),
  };
}

export function sortByDisplayOrder<T extends RepeatableInstance>(
  instances: T[],
): T[] {
  return [...instances].sort(
    (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0),
  );
}

/** Reorder within EXISTING or NEW section; reassigns global displayOrder. */
export function applySectionReorder<T extends RepeatableInstance>(
  sortedInstances: T[],
  section: RepeatableListSection,
  fromIndex: number,
  toIndex: number,
): T[] {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
    return sortedInstances;
  }

  const grouped = groupByStatus(sortedInstances);
  const existing = sortByDisplayOrder(grouped.existing);
  const newItems = sortByDisplayOrder(grouped.new);
  const sectionList = section === 'existing' ? existing : newItems;

  if (
    fromIndex >= sectionList.length ||
    toIndex >= sectionList.length ||
    sectionList.length < 2
  ) {
    return sortedInstances;
  }

  const reorderedSection = [...sectionList];
  const [moved] = reorderedSection.splice(fromIndex, 1);
  reorderedSection.splice(toIndex, 0, moved);

  const merged =
    section === 'existing'
      ? [...reorderedSection, ...newItems]
      : [...existing, ...reorderedSection];

  return merged.map((item, index) => ({
    ...item,
    displayOrder: index + 1,
  }));
}

const AUTO_NAME_PATTERN = /^(.+?)\s+(\d+)$/;

export function isAutoGeneratedInstanceName(
  name: string,
  prefix: string,
): boolean {
  const match = name.trim().match(AUTO_NAME_PATTERN);
  if (!match) {
    return false;
  }
  return match[1].toLowerCase() === prefix.trim().toLowerCase();
}

/** Serial label — e.g. "UPS 1", "UPS 2". */
export function generateAutoInstanceName(
  prefix: string,
  serial: number,
): string {
  return `${prefix.trim()} ${serial}`;
}

/** Pick the next unused auto name for the prefix. */
export function generateAutoInstanceNameFromTaken(
  prefix: string,
  takenNames: Iterable<string | undefined> = [],
): string {
  const normalizedPrefix = prefix.trim().toLowerCase();
  const usedSerials = new Set<number>();

  for (const raw of takenNames) {
    const trimmed = raw?.trim();
    if (!trimmed) {
      continue;
    }
    const match = trimmed.match(AUTO_NAME_PATTERN);
    if (match && match[1].toLowerCase() === normalizedPrefix) {
      const serial = parseInt(match[2], 10);
      if (Number.isFinite(serial)) {
        usedSerials.add(serial);
      }
    }
  }

  let serial = 1;
  while (usedSerials.has(serial)) {
    serial += 1;
  }
  return generateAutoInstanceName(prefix, serial);
}

function normalizeNameKey(name: string | undefined): string | null {
  const trimmed = name?.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

/** Unique duplicate name — user names get `-copy`; auto names get a fresh serial. */
export function buildDuplicateInstanceName(
  sourceName: string | undefined,
  prefix: string,
  takenNames: Iterable<string | undefined> = [],
): string {
  const trimmed = sourceName?.trim();

  if (trimmed && !isAutoGeneratedInstanceName(trimmed, prefix)) {
    const taken = new Set(
      [...takenNames]
        .map(normalizeNameKey)
        .filter((name): name is string => name != null),
    );

    let candidate = `${trimmed}-copy`;
    if (!taken.has(candidate.toLowerCase())) {
      return candidate;
    }

    let suffix = 2;
    while (taken.has(`${trimmed}-copy-${suffix}`.toLowerCase())) {
      suffix += 1;
    }
    return `${trimmed}-copy-${suffix}`;
  }

  return generateAutoInstanceNameFromTaken(prefix, takenNames);
}

/** Primary list title — user name, else "{prefix} N" from displayOrder. */
export function getInstanceNamePrimaryDisplay(
  instance: RepeatableInstance,
  prefix: string,
): string {
  const name = instance.instanceName?.trim();
  if (name) {
    return name;
  }
  if (instance.displayOrder != null && instance.displayOrder > 0) {
    return generateAutoInstanceName(prefix, instance.displayOrder);
  }
  return '—';
}

export function instanceIdsEqual(
  left: number | undefined,
  right: number | undefined,
): boolean {
  if (left == null || right == null) {
    return false;
  }
  return Number(left) === Number(right);
}

/** Client-only id for a row synthesized from legacy flat request columns. */
export const LEGACY_INSTANCE_SYNTHETIC_ID = -1;

export function canDeleteInstanceFromList(
  instance: RepeatableInstance,
  isDraftMode: boolean,
): boolean {
  if (instance.id == null) {
    return false;
  }
  if (isDraftMode) {
    return true;
  }
  if (instance.id < 0 && instance.id !== LEGACY_INSTANCE_SYNTHETIC_ID) {
    return true;
  }
  return instance.id > 0 && instance.id !== LEGACY_INSTANCE_SYNTHETIC_ID;
}

/** Format empty table cells consistently. */
export function formatRepeatableTableCell(
  value: string | number | boolean | null | undefined,
): string {
  if (value == null) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '—';
  }
  const trimmed = String(value).trim();
  return trimmed || '—';
}
